import { useEffect, useMemo } from 'react'
import { fetchBasicOrder } from 'actions/OrderActions'
import { useAppDispatch, useAppSelector, shallowEqual } from 'hooks/reduxHooks'
import { getIsEnabledFlightsLuxPlusPreview, getIsEnabledFlightsLuxPlusPricing } from 'luxPlus/selectors/featureToggle'

// exposable → hotel/other item; safe to show the amount + count it in the total
// flight    → flight item; amount withheld (a follow-up renders a "Sign up to view saving" CTA)
// pending   → order not loaded/errored; fail closed, amount withheld, not counted
export type PotentialSavingsRowStatus = 'exposable' | 'flight' | 'pending'

export interface PotentialSavingsRow {
  item: App.LuxPlusPotentialSavingsItem
  status: PotentialSavingsRowStatus
}

interface UsePotentialSavingsRowsResult {
  rows: Array<PotentialSavingsRow>
  totalAmount: number
  hasFlightSavings: boolean
  fetching: boolean
}

/**
 * Flights: LuxPlus+ flight potential savings are stored + API-exposed but the
 * amount must not be shown to customers by default (member-vs-public flight
 * prices aren't exposable). The potential-savings API carries no item type, so
 * we join each row to its order to classify it (mirrors useOrderItemName's
 * flight branch, over the whole list — hooks can't run per-row in a loop).
 * Flight rows are tagged so the modal renders a masked "Sign up to view saving"
 * CTA and their amount is excluded from the total. Fail closed: a row is
 * 'exposable' only once its order has loaded AND is confirmed non-flight.
 *
 * Region gate: the master flights LuxPlus+ pricing flag (getIsEnabledFlightsLuxPlusPricing) is
 * region-scoped via Optimizely, but svc-order/svc-flights store the saving regardless of region.
 * Where the flag is off (e.g. US) flight rows are dropped entirely — neither shown nor counted —
 * so a customer isn't shown a saving for a product not offered in their region.
 *
 * Exception: when the `flightsLuxPlusPreview` Optimizely flag is on
 * (getIsEnabledFlightsLuxPlusPreview), flight amounts MAY be shown, so flights
 * are treated as 'exposable' like any other vertical (amount + counted in total).
 * Remove once the API returns an item type (or excludes flights server-side).
 */
export default function usePotentialSavingsRows(
  items: Array<App.LuxPlusPotentialSavingsItem>,
): UsePotentialSavingsRowsResult {
  const dispatch = useAppDispatch()

  const orderIds = useMemo(
    () => Array.from(new Set(items.map(item => item.orderId).filter(Boolean))) as Array<string>,
    [items],
  )

  // Fetch each referenced order so we can classify flight vs non-flight. We only need
  // order.flightItems, which is on the basic order — so use fetchBasicOrder (one request via
  // getOrderById) rather than fetchOrder/getFullOrder, which fires ~8 coupled requests and fails
  // as a whole if any one errors, leaving the row stuck 'pending' and the CTA hidden on first load.
  useEffect(() => {
    orderIds.forEach(orderId => dispatch(fetchBasicOrder(orderId)))
  }, [orderIds, dispatch])

  // Scope the selectors to the orders this list references (with shallowEqual) so
  // the component doesn't re-render on unrelated order loads elsewhere in the app.
  const orders = useAppSelector(state => {
    const result: Record<string, App.Order> = {}
    for (const id of orderIds) {
      const order = state.orders.orders[id]
      if (order) result[id] = order
    }
    return result
  }, shallowEqual)
  const orderErrors = useAppSelector(state => {
    const result: Record<string, unknown> = {}
    for (const id of orderIds) {
      if (state.orders.orderErrors[id]) result[id] = state.orders.orderErrors[id]
    }
    return result
  }, shallowEqual)

  // When the flights free-preview pricing experiment is on, flight amounts are exposable.
  const flightPreviewPricingEnabled = useAppSelector(getIsEnabledFlightsLuxPlusPreview)
  // The master flights LuxPlus+ pricing flag is region-gated (Optimizely). svc-order/svc-flights
  // are NOT region-aware and store the saving regardless, so we gate the display here.
  const flightPricingEnabled = useAppSelector(getIsEnabledFlightsLuxPlusPricing)

  return useMemo(() => {
    const rows: Array<PotentialSavingsRow> = items
      .map((item): PotentialSavingsRow | null => {
        const order = item.orderId ? orders[item.orderId] : undefined
        // No orderId → can't verify it isn't a flight, so withhold it (fail closed).
        // In practice order_id is always present (NOT NULL unique key); this is a guard.
        if (!order) return { item, status: 'pending' }
        const isFlight = order.flightItems?.some(flightItem => flightItem.id === item.itemId)
        if (!isFlight) return { item, status: 'exposable' }
        // Flights LuxPlus+ pricing is off in this region (the FE flag is region-gated but the stored
        // saving isn't) — drop the flight row entirely so it is neither shown nor counted. Otherwise
        // a customer who happened to book a discounted flight would see a saving for a product that
        // isn't offered to them.
        if (!flightPricingEnabled) return null
        // Flights are masked ('flight') unless the preview-pricing flag lets us show the amount.
        return { item, status: flightPreviewPricingEnabled ? 'exposable' : 'flight' }
      })
      .filter((row): row is PotentialSavingsRow => row !== null)

    const totalAmount = rows
      .filter(row => row.status === 'exposable')
      .reduce((sum, row) => sum + (row.item.displayAmount ?? 0), 0)

    // An order is settled once it has loaded or errored; otherwise we're still waiting on it.
    const fetching = orderIds.some(orderId => !orders[orderId] && !orderErrors[orderId])

    return {
      rows,
      totalAmount,
      hasFlightSavings: rows.some(row => row.status === 'flight'),
      fetching,
    }
  }, [items, orders, orderErrors, orderIds, flightPreviewPricingEnabled, flightPricingEnabled])
}
